/**
 * Markdown-protection helpers. Identify content that must stay verbatim — fenced/inline code,
 * existing Markdown links/images, bracketed URLs, and `<a>` tags — so linkifiers never rewrite text
 * inside a code block or an existing link.
 *
 * Two consumption styles, because they have different needs:
 *  - {@link withProtectedMarkdown} hides the protected spans behind placeholders, runs a transform
 *    over the rest, then restores them. Use when the transform doesn't care about character offsets
 *    or per-match ordering (URL/phone linkification, the streaming citation skeleton).
 *  - {@link getProtectedRanges} returns the offset ranges instead, so a transform can keep iterating
 *    the FULL text (preserving per-match index/ordering) while choosing not to act inside a range.
 *    Use for `linkifyContractCitations`, whose marker count must mirror the backend's `citations[]`
 *    ordering — the backend counts every `[n]` regardless of context, so the FE must too, and may
 *    only skip *wrapping* (not counting) markers that land in protected spans.
 */
// The Markdown-link alternative tolerates backslash-escaped characters in the link text, because
// the citation linkifiers inject links whose text is an escaped marker (e.g. `[\[1\]](#cite-0)`)
// and those must stay protected from the later contact-linkify pass.
const PROTECTED_MARKDOWN_PATTERN =
	/```[\s\S]*?```|`[^`\n]+`|\[\[[^\n]*(?:https?:\/\/|www\.)[^\n]*]|!?\[(?:\\[^\n]|[^\][\n\\])+]\([^)]+\)|\[[^\]\n]*(?:https?:\/\/|www\.)[^\]\n]*]|<a\b[^>]*>[\s\S]*?<\/a>/gi;

/** Character ranges `[start, end)` of protected spans in `text`, in document order. */
export function getProtectedRanges(text: string): Array<[number, number]> {
	const ranges: Array<[number, number]> = [];
	for (const match of text.matchAll(PROTECTED_MARKDOWN_PATTERN)) {
		if (match.index !== undefined) ranges.push([match.index, match.index + match[0].length]);
	}
	return ranges;
}

/** Whether a character offset falls inside any protected range. */
export const isWithinProtectedRange = (offset: number, ranges: Array<[number, number]>): boolean =>
	ranges.some(([start, end]) => offset >= start && offset < end);

export const withProtectedMarkdown = (text: string, transform: (value: string) => string): string => {
	const protectedParts: string[] = [];
	const placeholderPrefix = ' PROTECTED_MARKDOWN_';
	const placeholderSuffix = ' ';
	const protectedText = text.replace(PROTECTED_MARKDOWN_PATTERN, (match) => {
		const index = protectedParts.push(match) - 1;
		return `${placeholderPrefix}${index}${placeholderSuffix}`;
	});

	return transform(protectedText).replace(
		new RegExp(`${placeholderPrefix}(\\d+)${placeholderSuffix}`, 'g'),
		(_placeholder, index: string) => protectedParts[Number(index)],
	);
};
